import { Achievements } from '@cardsjd/achievements';
import { Analytics } from '@cardsjd/analytics';
import ConfigManager from '@cardsjd/configmanager';
import { Device } from '@cardsjd/device';
import { EmailFeedback } from '@cardsjd/emailfeedback';
import { IAP } from '@cardsjd/iap';
import { Localization } from '@cardsjd/localization';
import { MarketPlace } from '@cardsjd/marketplace';
import { Prompter } from '@cardsjd/prompter';
import { Portal } from '@cardsjd/portal';
import { RateApp } from '@cardsjd/rateapp';
import { Share } from '@cardsjd/share';
import { Storage } from '@cardsjd/storage';

import AchievementConfig from './configs/AchievementConfig.js';
import AppConfig from './configs/AppConfig.js';
import LocalizationConfig from './configs/LocalizationConfig.js';
import StoreConfig from './configs/StoreConfig.js';
import Multiplayer from './services/Multiplayer.js';
import { updateLevel } from './services/level.js';

/**
 * Get a random integer between min (inclusive) and max (exclusive)
 * @param {number} min 
 * @param {number} max 
 * @returns number
 */
export function getRandomInt(min, max) {
	const minCeiled = Math.ceil(min);
	const maxFloored = Math.floor(max);
	return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled);
}

// const NUMBERS = '0123456789';
// function generateId(length = 6, characters = NUMBERS) {
// 	let result = '';

// 	for (let i = 0; i < length; ++i) {
// 		result = `${result}${characters.charAt(Math.floor(Math.random() * characters.length))}`;
// 	}

// 	return result;
// }

/**
 * Get a random avatar
 * @returns string URL of random avatar
 */
function getRandomAvatarURL() {
	return `AvatarVV_${getRandomInt(1, 233).toString().padStart(4, '0')}.png`;
}

export default class App {

	constructor() {
		this.userdata = {};
		this.device = new Device();
		this.os = Device.getOS();
		this.platform = AppConfig.platform || Device.getPlatform();
		this.config = ConfigManager.resolve(AppConfig, this.platform, this.os);
		this.config.platform = this.platform;
		this.pageId = 'Loading';

		this.onNavigate = function (_path) { };

		this.onNotification = function (_model) { };

		this.onPopUp = function (_model, _template) { };

		this.debug = this.config.debugMode;
		this.version = this.config.version;

		//libraries
		this.localization = new Localization(LocalizationConfig);
		this.marketPlace = new MarketPlace(this.config);
		this.rateApp = new RateApp(this.config);
		this.emailFeedback = new EmailFeedback(this.config);
		this.sharer = new Share(this.config);
		this.analytics = new Analytics(this.config);
		this.achievements = new Achievements(AchievementConfig);
		this.achievements.onNewAchievement = this.onNewAchievement.bind(this);
		this.prompter = new Prompter(this.config);
		this.prompter.onPrompt = this.onPrompt.bind(this);
		this.storage = new Storage(this.config);
		this.portal = Portal.create(this.config, this.platform);
		this.iap = new IAP(StoreConfig);

		this.portal.onAdError = (error) => console.log('ad error', error);
		this.portal.onAdOpened = (details) => console.log('ad opened', details);

		//get default resource
		this.resource = this.localization.getResource();

		//get device info
		this.browser = this.device.getBrowser();

		this.iap.onBuy = this.onBuy.bind(this);

		this.multiplayer = new Multiplayer(this.config);
		this.multiplayer.on(Multiplayer.Events.CHAT_RECEIVED, (message) => {
			if (this.debug) {
				console.log('got message on chat', message);
			}
		});

		this.changeListeners = new Set();
	}

	get showMoreGames() {
		return this.config.showMoreGames && this.marketPlace.hasGames() && !this.userdata.fullVersion;
	}

	//Platform availability comes from the flattened app config.
	get showRemoveAds() {
		return this.config.showRemoveAds && !this.userdata.removeAds && this.iap?.isAvailable && !this.userdata.fullVersion;
	}

	async load() {
		this.navigate('Loading');

		//load userdata
		const data = await this.storage.load(this.config.storageName);

		if (data) {
			this.userdata = data;
		}

		await this.loadPortalData();
		this.updateLevel();

		//set localization resource based on user settings
		if (this.userdata.language) {
			this.resource = this.localization.getResource(this.userdata.language);
		}

		//tracking
		this.analytics.trackStart();
		//todo: firebase track user

		//ask to rate / share
		if (this.config.showRateGame) {
			this.prompter.delayedPrompt();
		}

		//save os in profile
		this.userdata.os = Device.getOS();
		this.userdata.platform = Device.getPlatform();
		this.userdata.browser = this.device.getBrowser();
		this.userdata.version = this.version;

		this.userdata.isMuted = this.userdata.isMuted ?? this.config.isMuted;

		//log game opened
		this.userdata.game_open = (this.userdata.game_open ?? 0) + 1;

		if (!this.userdata.first_open) {
			this.userdata.first_open = Date.now();
		}
		//todo: user id will come from server
		// if (!this.userdata.id) {
		// 	this.userdata.id = generateId();
		// }

		if (!this.userdata.name) {
			this.userdata.name = `Player`;
		}

		if (!this.userdata.avatar) {
			this.userdata.avatar = getRandomAvatarURL();
		}

		//check for achievements
		this.achievements.checkForAchievementAndReward(this.userdata, this.userdata);

		this.iap.init().catch(e => console.log('IAP.init() failed', e));

		//todo: Push Notifications

		//todo:black list check

		//set up the ad implementation selected by the active portal
		this.portal.setupAds().catch(e => console.log('setupAds failed', e));


		this.navigate('MainMenu');
		console.log('Application loaded using', this);
	}

	navigate(path) {
		this.pageId = path;

		//trigger UI contollers
		if (this.onNavigate) {
			this.onNavigate(path);
		}

		this.navigated(path);
	}

	navigated(path) {
		if (path == '') {
			path = 'MainMenu';
		}

		if (this.debug) {
			console.log('navigated', path);
		}

		//todo: firebase track page;

		if (path == 'MainMenu') {
			this.showAd();
			this.save();
		}

		if (path == 'ComputerMenu') {
			this.showAd();
			this.save();
		}

		if (path == 'OnlineMenu') {
			this.showAd();
			this.save();
		}
	}

	onNewAchievement(achievements) {
		//this.showAchievementPopup(achievements);
		this.save();

		for (let i = 0; i < achievements.length; i++) {
			const achievement = achievements[i];
			this.showNotification({
				title: achievement.title,
				description: achievement.description,
				image: achievement.image,
				points: achievement.points,
				type: 'Achievement',
			});
		}

		// Trigger application context update to refresh MainMenu and other components
		this.handleDataChange();
	}

	updateLevel() {
		updateLevel(this.userdata);
	}

	async save() {
		await this.storage.save(this.config.storageName, this.userdata);
		try {
			await this.portal.save(this.userdata);
		} catch (error) {
			console.log('Portal save failed', error);
		}
	}

	saveGameState(gameState) {
		this.storage.save(this.config.gameStorageName, gameState);
	}

	getSavedGameState() {
		return this.storage.load(this.config.gameStorageName);
	}

	async showAd(force) {
		if (force) {
			await this.portal.setupAds();
			await this.portal.showInterstitialAd();
			return;
		}

		if (this.userdata.fullVersion || this.userdata.removeAds) {
			return;
		}

		await this.portal.tryShowInterstitialAd();
	}

	async showRewardAd() {
		await this.portal.showRewardedAd();
	}

	rateGame() {
		this.achievements.awardAchivement(this.userdata, 'Rate Game');
		const results = this.rateApp.rate();

		if (!results) {
			this.share();
		}
	}

	email() {
		this.achievements.awardAchivement(this.userdata, 'Send Feedback');
		this.emailFeedback.send(this.userdata);
	}

	share() {
		this.achievements.awardAchivement(this.userdata, 'Share Game');
		this.sharer.share();
	}

	delay(n) {
		return new Promise(function (resolve) {
			setTimeout(resolve, n * 1000);
		});
	}

	showNotification(model) {
		//Basic - { message: "Message", type: "Basic" }
		//Achievement - { message: "Test", description: "Something more", points: 25, type:"Achievement" }
		if (typeof model === 'string') {
			model = {
				message: model,
				type: 'Basic',
			};
		}

		if (this.onNotification) {
			this.onNotification(model);
		}
	}

	showPopUp(model, template) {
		if (this.onPopUp) {
			this.onPopUp(model, template);
		}
	}

	showEnjoyGame(message) {
		if (!message) {
			message = 'Having fun?';
		}

		this.showPopUp({
			message: message,
			onYes: this.showAskRate.bind(this),
		}, 'YesNoDialog');
	}

	showShare(message) {
		if (!message) {
			message = 'Share the app?';
		}

		this.showPopUp({
			message: message,
			onYes: this.share.bind(this),
		}, 'YesNoDialog');
	}

	showAskRate(message) {
		if (!message) {
			message = 'Rate the app?';
		}

		this.showPopUp({
			message: message,
			onYes: this.rateGame.bind(this),
		}, 'YesNoDialog');
	}

	onPrompt(message, os) {
		if (os == 'FBMessenger') {
			this.showShare(message);
		} else {
			//ask if like game then ask to rate
			this.showEnjoyGame(message);
		}

		this.rateApp.rateNative();
	}

	setIsMuted(isMuted) {
		this.userdata.isMuted = isMuted;
		this.save();
	}

	onBuy(productId) {
		if (productId.includes('removeads')) {
			if (!this.userdata.removeAds) {
				this.showNotification('Ads have been removed');
			}

			this.userdata.removeAds = true;
		}

		if (productId.includes('fullversion')) {
			if (!this.userdata.fullVersion) {
				this.showNotification('Full version is enabled');
			}

			this.userdata.fullVersion = true;
		}

		this.save();
		this.handleDataChange();
	}

	setRemoveAds(shouldRemoveAds) {
		this.userdata.removeAds = shouldRemoveAds;
		this.save();
	}

	setLanguage(newLanguage) {
		this.userdata.language = newLanguage;
		this.save();
	}

	resetStats() {
		this.userdata.stats = {};
		this.updateLevel();
		this.save();
		this.showNotification('Stats have been reset');
	}

	async loadPortalData() {
		let portalData;
		try {
			await this.portal.init();
			await this.portal.loadStart();
			portalData = await this.portal.load();
		} catch (error) {
			console.log('Portal unavailable', error);
		} finally {
			try {
				await this.portal.loadStop();
			} catch (error) {
				console.log('Portal load completion failed', error);
			}
		}

		if (portalData?.userdata && Object.keys(this.userdata).length === 0) {
			this.userdata = portalData.userdata;
		}

		const portalUser = portalData?.user;
		if (portalUser) {
			this.userdata.facebookId = portalUser.facebookId ?? this.userdata.facebookId;
			this.userdata.name = portalUser.name || this.userdata.name;
			this.userdata.avatar = portalUser.avatar || this.userdata.avatar;
			this.userdata.avatar_facebook = portalUser.facebookId && portalUser.avatar
				? portalUser.avatar
				: this.userdata.avatar_facebook;
			this.userdata.locale = portalUser.locale || this.userdata.locale;
		}

		if (await this.portal.getInvite()) {
			this.showNotification('You have been invited to play a game');
		}
	}

	handleDataChange() {
		//will be overridden by AppContextProvider
	}
}
